/** Общие мелочи для интерфейса серверов: цвет и инициалы иконки, непрочитанное. */
import { ChannelType, type Channel, type Member, type ReadState } from '@/types/api'
import { compareIds, displayName } from '@/store/social'

export const guildColor = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 45% 45%)`

export function guildInitials(name: string): string {
  const words = name.replace(/[«»"'()]/g, '').split(/\s+/).filter(Boolean)
  const letters = words.slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '')
  return letters.join('') || name.slice(0, 2).toUpperCase()
}

/** Есть ли в канале непрочитанные: последнее сообщение новее отмеченного прочитанным. */
export function isUnread(ch: Channel, rs: ReadState | undefined): boolean {
  if (!ch.last_message_id) return false
  if (!rs?.last_message_id) return true
  return compareIds(ch.last_message_id, rs.last_message_id) > 0
}

/**
 * Перестановка сервера в колонке: перетащили dragId на targetId — встаёт на его место,
 * остальные сдвигаются. Возвращает новый порядок, исходный массив не трогает.
 */
export function moveInOrder(ids: string[], dragId: string, targetId: string): string[] {
  const next = [...ids]
  const from = next.indexOf(dragId)
  const to = next.indexOf(targetId)
  if (from < 0 || to < 0 || from === to) return ids
  next.splice(to, 0, ...next.splice(from, 1))
  return next
}

export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

/** Имя участника на сервере: ник, иначе отображаемое имя. */
export function memberName(m: Member): string {
  return m.nick ?? displayName(m.user)
}

/** Ссылка-приглашение для текущего адреса сайта. */
export function inviteLink(code: string): string {
  return `${location.origin}/invite/${code}`
}

/** Сроки автоархива веток (минуты → подпись). */
export const THREAD_ARCHIVE_OPTIONS: { value: number; label: string }[] = [
  { value: 60, label: '1 час' },
  { value: 1440, label: '24 часа' },
  { value: 4320, label: '3 дня' },
  { value: 10080, label: '7 дней' },
]

export function archiveLabel(minutes: number | undefined): string {
  return THREAD_ARCHIVE_OPTIONS.find((o) => o.value === minutes)?.label ?? '24 часа'
}

/** Активные ветки канала, новые сверху. */
export function threadsOf(channels: Record<string, Channel> | undefined, parentId: string): Channel[] {
  return Object.values(channels ?? {})
    .filter((c) => c.type === ChannelType.PublicThread && c.parent_id === parentId && !c.thread_metadata?.archived)
    .sort((a, b) => -compareIds(a.last_message_id ?? a.id, b.last_message_id ?? b.id))
}

/** Название ветки по умолчанию — начало сообщения. */
export function defaultThreadName(content: string): string {
  const text = content.replace(/<@&?!?\d+>|<#\d+>|https?:\/\/\S+|[*_~`|]/g, '').replace(/\s+/g, ' ').trim()
  return text.length > 50 ? `${text.slice(0, 47).trimEnd()}…` : text
}

/** Баннер сервера над каналами — со 2-го уровня бустов, анимированный — с 3-го (BANNER и ANIMATED_BANNER у Discord). */
export const BANNER_TIER = 2
export const ANIMATED_BANNER_TIER = 3
